/**
 * CxL3 — busduct / busway.
 *
 * Two things make this different from a switchboard.
 *
 * **Thermography is on by default.** Everywhere else in the catalogue a thermal
 * survey is off by default, because startup rarely has enough load to make one
 * meaningful. On busduct it stays on: a bolted joint that was not torqued is
 * invisible to every other test here and shows up as heat and nothing else. If
 * there is no load to survey under, that is a reason to record the limitation,
 * not to skip the test.
 *
 * **The tap-offs are the register.** A busway run is one asset with many tap-off
 * units hanging from it, each its own way with its own protection — so the
 * tap-off tests are breaker-scope and the run tests are board-scope.
 *
 * Draft defaults. No CxA busduct script has been issued yet.
 */

import type { CatalogueTest } from '../catalogue.js';

export const CXL3_BUSDUCT: readonly CatalogueTest[] = [
  {
    code: 'BD-VIS',
    name: 'Run inspection and readiness',
    cxLevel: 'CxL3',
    equipmentTypes: ['Busduct'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Visual Inspection',
    checks: [
      { label: 'Rating and route match the approved layout', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Joint torque marks present and intact at every joint', responseType: 'pass_fail_na', toleranceMode: 'none', acceptanceCriteria: 'Every joint marked — an unmarked joint is an untorqued joint', requiresPhoto: true },
      { label: 'Joint covers fitted and secure', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Supports and hangers at the designed spacing, expansion units free', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Fire barriers made good at every compartment penetration', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Housing earth continuity across the run and at both ends', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Phase identification consistent end to end', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Run clear of water ingress, storage and temporary works', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['BD-VIS-PLATE', 'BD-VIS-JOINT', 'BD-VIS-FIRESTOP', 'BD-VIS-EARTH'],
  },
  {
    code: 'BD-VT',
    name: 'Voltage at the run ends',
    cxLevel: 'CxL3',
    equipmentTypes: ['Busduct'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Voltage Test',
    note: 'Taken at the far end as well as the source, so a volt drop or a lost phase along the run is visible.',
    checks: [
      { label: 'Source end L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Source end L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Source end L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Far end L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Far end L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Far end L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Volt drop source to far end', responseType: 'measured_pass_fail', unit: '%', tolerance: 2.5, toleranceMode: 'max', acceptanceCriteria: '≤ 2.5% unless the design states otherwise' },
    ],
    shotCodes: ['BD-VT-SOURCE', 'BD-VT-FAREND'],
  },
  {
    code: 'BD-THERMO',
    name: 'Thermographic survey of joints',
    cxLevel: 'CxL3',
    equipmentTypes: ['Busduct'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Visual Inspection',
    note: 'On by default here. Record the load at the time — a clean survey at 2% load proves very little and the report should say so.',
    checks: [
      { label: 'Every accessible joint surveyed', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Maximum joint temperature rise above ambient', responseType: 'measured_pass_fail', unit: '°C', tolerance: 10, toleranceMode: 'max', acceptanceCriteria: '≤ 10 °C rise between comparable joints' },
      { label: 'Load at the time of survey', responseType: 'measured_pass_fail', unit: '%', toleranceMode: 'none' },
      { label: 'Joints not accessible for survey listed in remarks', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['BD-THERMO-JOINT', 'BD-THERMO-HOTTEST'],
  },
  {
    code: 'BD-TAPOFF',
    name: 'Tap-off unit operation',
    cxLevel: 'CxL3',
    equipmentTypes: ['Busduct'],
    breakerTypes: ['Tap-off'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Per tap-off. The plug-in interlock is the safety check: a unit that can be withdrawn live is a defect, not a convenience.',
    checks: [
      { label: 'Tap-off rating and protective device match the schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Plug-in interlock prevents insertion or withdrawal while closed', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Unit refuses to move until the device is open', requiresPhoto: true },
      { label: 'Protective device ON / OFF / trip operation', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Operates and indicates correctly in each position' },
      { label: 'Outgoing voltage at the tap-off', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Phase rotation at the tap-off', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Earth continuity from housing to the tap-off unit', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Labelling identifies the circuit it feeds', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['BD-TAP-PLATE', 'BD-TAP-INTERLOCK', 'BD-TAP-PR', 'BD-TAP-LABEL'],
  },
];
